import { useMutation } from '@tanstack/react-query';
import { downloadReportPdf } from '../common/api/reports';
import { Button } from '@cloudscape-design/components';

type ReportSource = 'google' | 'yelp' | 'bbb' | 'dealerrater' | 'carscom' | 'carfax' | 'edmunds' | 'cargurus' | 'opentable' | 'tripadvisor' | 'booking' | 'expedia' | 'golfpass' | 'ai';

const SOURCE_DISPLAY: Record<string, string> = {
  google: 'Google', yelp: 'Yelp', bbb: 'BBB', dealerrater: 'DealerRater',
  carscom: 'Cars.com', carfax: 'CarFax', edmunds: 'Edmunds', cargurus: 'CarGurus',
  opentable: 'OpenTable', tripadvisor: 'TripAdvisor', booking: 'Booking.com',
  expedia: 'Expedia', golfpass: 'GolfPass', ai: 'AI', combined: 'All-Sources',
};

const PERIOD_DISPLAY: Record<string, string> = {
  '30-days': 'Past-30-Days',
  '60-days': 'Past-60-Days',
  '90-days': 'Past-90-Days',
  '180-days': 'Past-180-Days',
};

function safe(value: string | undefined | null): string {
  if (!value) return '';
  return value.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * Build the user-facing PDF filename. Matches the backend's
 * `scorecard_pdf_filename` (app/services/pdf_render.py): includes the
 * place address so multi-location chains with the same business name
 * are distinguishable in the user's downloads folder.
 *
 * Example:
 *   `Toyota-of-Runnemede-855-Black-Horse-Pike-Runnemede-NJ-All-Sources-Past-180-Days-2026-05-21.pdf`
 */
export function buildPdfFilename(args: {
  placeName?: string;
  placeAddress?: string;
  reportDate: Date;
  source: string;
  period: string;
}): string {
  const parts: string[] = [];
  parts.push(safe(args.placeName) || 'scorecard');
  const addr = safe(args.placeAddress);
  if (addr) parts.push(addr);
  parts.push(safe(SOURCE_DISPLAY[args.source] ?? args.source));
  parts.push(safe(PERIOD_DISPLAY[args.period] ?? args.period));
  // ISO yyyy-mm-dd so files sort chronologically in the downloads folder
  const iso = args.reportDate.toISOString().slice(0, 10);
  parts.push(iso);
  return parts.filter(Boolean).join('-') + '.pdf';
}

type ReportDownloadItem = {
  uuid: string;
  created_at: string;
  place?: { name?: string; address?: string } | null;
};

export type ReportDownloadButtonProps = {
  item: ReportDownloadItem;
  period: string;
  variant?: 'button' | 'link' | 'icon' | 'inline-link' | 'inline-icon';
  sources?: ReportSource[];
  defaultSource?: ReportSource | 'combined';
  /**
   * Optional download function override. Defaults to the per-place
   * `downloadReportPdf` (calls /reports/{uuid}/{source}/{period}/pdf).
   * Chain scorecards pass `downloadChainReportPdf` which hits the
   * /chains/reports/... endpoint. Same button UI either way.
   */
  downloadFn?: (uuid: string, source: string, period: string, customerVolume?: number) => Promise<string>;
  customerVolume?: number;
};


export function ReportDownloadButton(props: ReportDownloadButtonProps) {
  const { item, period } = props;

  const defaultSource = props.defaultSource ?? 'combined';
  const downloadFn = props.downloadFn ?? downloadReportPdf;


  const downloadQuery = useMutation({
    mutationFn: (source: string) => downloadFn(item.uuid, source, period, props.customerVolume),
    onSuccess: (data: string, source: string) => {
      if (data) {
        const url = data;
        const a = document.createElement('a');
        a.href = url;
        a.download = buildPdfFilename({
          placeName: item.place?.name,
          placeAddress: item.place?.address,
          reportDate: new Date(item.created_at + 'Z'),
          source,
          period,
        });
        a.target = '_blank';
        a.click();

        URL.revokeObjectURL(url);
      }
    },
  });

  const handleDownload = (source: string) => {
    downloadQuery.mutate(source);
  };


  // if (props.variant === 'button') {
  //   return (
  //     <ButtonDropdown
  //       variant="primary"
  //       disabled={downloadQuery.isPending || dropdownItems.length === 0}
  //       loading={downloadQuery.isPending}
  //       items={dropdownItems}
  //       onItemClick={({ detail }) => handleDownload(detail.id)}
  //       mainAction={{
  //         text: 'Download PDF',
  //         onClick: () => handleDownload(defaultSource),
  //       }}
  //     />
  //   );
  // }

  const isInline = props.variant === 'inline-link' || props.variant === 'inline-icon';
  const buttonVariant = isInline ? props.variant as 'inline-link' | 'inline-icon' : 'primary';

  return (
    <Button
      variant={buttonVariant}
      onClick={() => {
        handleDownload(defaultSource);
      }}
      iconName="download"
      loading={downloadQuery.isPending}
    >
      {isInline ? undefined : 'Download PDF'}
    </Button>
  );
}
